import React from 'react';
import { ArrowRightIcon, MessageSquareIcon, CheckIcon } from 'lucide-react';
import { openCalendlyPopup } from '../utils/calendly';
import { useLanguage } from '../contexts/LanguageContext';
export function Hero() {
  const {
    t
  } = useLanguage();
  return <section className="relative pt-24 lg:pt-32 pb-16 lg:pb-24 bg-[#0a0e1a] overflow-hidden" data-id="element-151">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#ff6b35]/5 via-transparent to-transparent" data-id="element-152" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-153">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-154">
          {/* Text Content */}
          <div className="text-center lg:text-left" data-id="element-155">
            <div className="inline-flex items-center gap-2 bg-[#ff6b35]/10 border border-[#ff6b35]/20 rounded-full px-4 py-1.5 mb-6" data-id="element-156">
              <span className="text-[#ff6b35] text-sm font-medium" data-id="element-157">
                {t('hero.badge')}
              </span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6" data-id="element-158">
              {t('hero.title')}
            </h1>

            <p className="text-lg lg:text-xl text-gray-400 mb-6 max-w-xl mx-auto lg:mx-0" data-id="element-159">
              {t('hero.subtitle')}
            </p>

            {/* Trust indicators */}
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-4 mb-8 text-sm text-gray-400" data-id="element-160">
              <div className="flex items-center gap-2" data-id="element-161">
                <CheckIcon className="w-4 h-4 text-[#ff6b35]" data-id="element-162" />
                <span data-id="element-163">{t('hero.customers')}</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-164">
                <CheckIcon className="w-4 h-4 text-[#ff6b35]" data-id="element-165" />
                <span data-id="element-166">{t('hero.experience')}</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-167">
                <CheckIcon className="w-4 h-4 text-[#ff6b35]" data-id="element-168" />
                <span data-id="element-169">{t('hero.gdpr')}</span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start" data-id="element-170">
              <button onClick={openCalendlyPopup} className="inline-flex items-center justify-center gap-2 bg-[#ff6b35] hover:bg-[#ff5722] text-white px-8 py-4 rounded-lg font-semibold transition-colors" data-id="element-171">
                {t('hero.cta.start')}
                <ArrowRightIcon className="w-5 h-5" data-id="element-172" />
              </button>
              <a href="#testimonials" className="inline-flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white px-8 py-4 rounded-lg font-semibold transition-colors" data-id="element-173">
                <MessageSquareIcon className="w-5 h-5" data-id="element-174" />
                {t('hero.cta.testimonials')}
              </a>
            </div>
          </div>

          {/* Dashboard Preview - Hidden on mobile/tablet */}
          <div className="relative hidden lg:block" data-id="element-175">
            <div className="relative bg-gradient-to-br from-[#1a1f36] to-[#111827] rounded-2xl border border-white/10 p-4 shadow-2xl" data-id="element-176">
              {/* Browser chrome */}
              <div className="flex items-center gap-2 mb-4" data-id="element-177">
                <div className="w-3 h-3 rounded-full bg-red-500/60" data-id="element-178" />
                <div className="w-3 h-3 rounded-full bg-yellow-500/60" data-id="element-179" />
                <div className="w-3 h-3 rounded-full bg-green-500/60" data-id="element-180" />
                <div className="flex-1 bg-white/5 rounded-md h-6 ml-4" data-id="element-181" />
              </div>

              {/* Dashboard content mockup */}
              <div className="space-y-4" data-id="element-182">
                <div className="grid grid-cols-3 gap-3" data-id="element-183">
                  <div className="bg-[#0a0e1a] rounded-lg p-4" data-id="element-184">
                    <div className="text-[#ff6b35] text-2xl font-bold" data-id="element-185">247</div>
                    <div className="text-gray-500 text-xs" data-id="element-186">Aktive Leads</div>
                  </div>
                  <div className="bg-[#0a0e1a] rounded-lg p-4" data-id="element-187">
                    <div className="text-green-400 text-2xl font-bold" data-id="element-188">
                      €89k
                    </div>
                    <div className="text-gray-500 text-xs" data-id="element-189">Pipeline</div>
                  </div>
                  <div className="bg-[#0a0e1a] rounded-lg p-4" data-id="element-190">
                    <div className="text-blue-400 text-2xl font-bold" data-id="element-191">34%</div>
                    <div className="text-gray-500 text-xs" data-id="element-192">Conversion</div>
                  </div>
                </div>

                {/* Chart placeholder */}
                <div className="bg-[#0a0e1a] rounded-lg p-4 h-32" data-id="element-193">
                  <div className="flex items-end justify-between h-full gap-2" data-id="element-194">
                    {[40, 65, 45, 80, 55, 90, 70, 85, 60, 95, 75, 88].map((height, i) => <div key={i} className="flex-1 bg-gradient-to-t from-[#ff6b35] to-[#ff6b35]/40 rounded-t" style={{
                    height: `${height}%`
                  }} data-id="element-195" />)}
                  </div>
                </div>

                {/* Table placeholder */}
                <div className="bg-[#0a0e1a] rounded-lg p-4 space-y-2" data-id="element-196">
                  {[1, 2, 3].map(i => <div key={i} className="flex items-center gap-3" data-id="element-197">
                      <div className="w-8 h-8 rounded-full bg-white/10" data-id="element-198" />
                      <div className="flex-1" data-id="element-199">
                        <div className="h-3 bg-white/10 rounded w-3/4 mb-1" data-id="element-200" />
                        <div className="h-2 bg-white/5 rounded w-1/2" data-id="element-201" />
                      </div>
                      <div className="px-2 py-1 bg-[#ff6b35]/20 text-[#ff6b35] text-xs rounded" data-id="element-202">
                        Hoch
                      </div>
                    </div>)}
                </div>
              </div>
            </div>

            {/* Floating elements */}
            <div className="absolute -top-4 -right-4 bg-[#1a1f36] border border-white/10 rounded-xl p-3 shadow-xl" data-id="element-203">
              <div className="text-green-400 text-sm font-semibold" data-id="element-204">+23%</div>
              <div className="text-gray-500 text-xs" data-id="element-205">Diesen Monat</div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}